// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.LazyLoadingTreeView.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, propTypes as ObservablePropTypes } from 'mobx-react';
import { asReference } from 'mobx';
import { curry, flatMap, isNil, invokeArgs } from 'lodash/fp';
import ScrollBox from '../Izenda.ScrollBox';
import FlattenTreeUIStore from './Izenda.FlattenTreeUIStore';
import LazyLoadingTreeViewItemList from './Izenda.LazyLoadingTreeViewItemList';

@observer
export default class LazyLoadingTreeView extends Component {
  static propTypes = {
    nodes: ObservablePropTypes.arrayOrObservableArray,
    getChildNodes: PropTypes.func.isRequired,
    getKey: PropTypes.func.isRequired,
    renderNode: PropTypes.func.isRequired,
    collapsed: PropTypes.bool,
    nodeExpand: PropTypes.func,
    getData: PropTypes.func.isRequired,
    isLastPage: PropTypes.bool.isRequired,
    registerLazyLoadingStore: React.PropTypes.func
  };

  static defaultProps = {
    collapsed: true
  };

  constructor(props) {
    super(props);
    this.flattenTree = new FlattenTreeUIStore();
  }

  setScrollboxRef = (element: any) => {
    this.scrollBox = element;
    invokeArgs('getTreeScrollbox', [element], this.props);
  };

  render() {
    const props = this.props;
    const { className, nodes, renderNode, getChildNodes, getKey, nodeExpand, collapsed } = props;

    const flattenFirstLevelNode = curry(flatten, 6)(
      0, // node level. Zero is for root node.
      nodeExpand, // check whether node is expanded
      getChildNodes, // retrieve child nodes of a particular tree node
      getKey, // retrieve key value of a node. It must be unique
      collapsed, // tree is collapsed by default
      null // parent tree node. Null is for root node.
    );

    const flattenNodes = flatMap(flattenFirstLevelNode, nodes);

    return (
      <ScrollBox ref={this.setScrollboxRef}>
        <LazyLoadingTreeViewItemList
          className={className}
          treeViewItemClassName={props.treeViewItemClassName}
          flattenNodes={flattenNodes}
          isLastPage={props.isLastPage}
          treeStore={this.flattenTree}
          renderNode={renderNode}
          getData={props.getData}
          viewHeight={props.viewHeight}
          itemHeight={props.itemHeight}
          defaultItemsNumber={props.defaultItemsNumber}
          registerLazyLoadingStore={props.registerLazyLoadingStore}
        />
      </ScrollBox>
    );
  }
}

function flatten(
  level: number,
  nodeExpand: boolean,
  getChildNodes: any,
  getKey: any,
  collapsed: boolean,
  parent: any,
  node: any
): Array<any> {
  if (!node) return [];

  const currentLevel = level;
  const key = getKey(node);
  let childNodeIndex, childrenLength, grandChildren;
  const nodeExpanded = !isNil(nodeExpand) && nodeExpand(node);
  const defaultExpanded = !collapsed;

  // Node is expanded if it's expanded by data logic, or by default setting
  // or by user actions.
  const shouldExpanded = nodeExpanded || defaultExpanded;
  const childNodes = getChildNodes(node) || [];
  const currentTreeNode = toTreeNode(currentLevel, key, shouldExpanded, childNodes.length, parent, node);
  const flattenList = [currentTreeNode];

  // Use native FOR loop to maximize performance while walking thru child nodes list.
  for (childNodeIndex = 0, childrenLength = childNodes.length; childNodeIndex < childrenLength; childNodeIndex++) {
    grandChildren = flatten(
      currentLevel + 1,
      nodeExpand,
      getChildNodes,
      getKey,
      collapsed,
      currentTreeNode,
      childNodes[childNodeIndex]
    );

    Array.prototype.push.apply(flattenList, grandChildren);
  }

  return flattenList;
}

function toTreeNode(
  level: number,
  key: string,
  shouldExpanded: boolean,
  childCount: number,
  parent: any,
  dataNode: any
) {
  return {
    level,
    data: asReference(dataNode),
    key,
    parent,
    childCount,
    expanded: shouldExpanded
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/LazyLoadingTreeView/Izenda.LazyLoadingTreeView.jsx